'use client';

import { useEffect, useState } from 'react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Scrollspy table of contents. Scans the nearest `.article-body` on mount,
 * generates heading IDs where missing, and tracks the currently-visible
 * section via an IntersectionObserver. A second observer hides the sidebar
 * when wide-breakout elements (images, comparison tables, post-article area)
 * are on screen so they get their full reading width.
 *
 * Lives as its own client island so the surrounding BlogArticle shell can
 * stay a server component.
 */
export function TableOfContents() {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [hidden, setHidden] = useState(false);

  // Build the TOC from the article body. Runs once on mount — article
  // content is static MDX, so there's nothing to watch for.
  useEffect(() => {
    const article = document.querySelector('.article-body');
    if (!article) return;

    const headings = article.querySelectorAll('h2, h3');
    const next: TocItem[] = [];
    headings.forEach((h) => {
      if (!h.id) {
        h.id =
          h.textContent
            ?.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '') || '';
      }
      next.push({
        id: h.id,
        text: h.textContent || '',
        level: h.tagName === 'H2' ? 2 : 3,
      });
    });
    setItems(next);
    const firstH2 = next.find((i) => i.level === 2);
    if (firstH2) setActiveId(firstH2.id);
  }, []);

  // Active-section scrollspy.
  useEffect(() => {
    if (items.length === 0) return;
    const article = document.querySelector('.article-body');
    if (!article) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '0px 0px -70% 0px', threshold: 0.1 },
    );

    const headings = article.querySelectorAll('h2, h3');
    headings.forEach((h) => observer.observe(h));

    return () => observer.disconnect();
  }, [items]);

  // Hide the TOC when wide-breakout blocks or the post-article footer
  // are on screen.
  useEffect(() => {
    if (items.length === 0) return;

    const targets = [
      ...document.querySelectorAll('.wide-breakout'),
      ...document.querySelectorAll('.cc-root'),
      ...document.querySelectorAll('.post-article'),
    ];
    if (targets.length === 0) return;

    const visible = new Set<Element>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        setHidden(visible.size > 0);
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="toc-sidebar">
      <aside className={`toc${hidden ? ' toc-hidden' : ''}`}>
        <div className="toc-label">On this page</div>
        <ul>
          {items
            .filter((item) => item.level === 2)
            .map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={activeId === item.id ? 'toc-active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(item.id)
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {item.text}
                </a>
              </li>
            ))}
        </ul>
      </aside>
    </div>
  );
}
